import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Helper } from '../../helpers/helper.service';
import { AuthenticationService } from 'src/app/shared/Services/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class ExchangeService {
  private readonly productOptionsCache = new Map<number, Observable<Array<{ id: number; name: string }>>>();

  constructor(
    private readonly http: HttpClient, 
    private readonly helper: Helper,
    private auth: AuthenticationService,
  ) {}

  getExchangeLogs(gridData: HttpParams = new HttpParams()): Observable<any> {
    const url = environment.apiUrl + '/exchanges/logs?' + gridData.toString();
    return this.http.get<any>(url).pipe(catchError((res) => this.helper.catchError(res)));
  }

  getExchanges(gridData: HttpParams = new HttpParams(), _params: HttpParams = new HttpParams()): Observable<any> {
    const url = environment.apiUrl + '/exchanges?' + gridData.toString();
    return this.http.get<any>(url).pipe(
      map((res) => {
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.exchange)
            ? res.exchange
            : Array.isArray(res?.exchanges)
              ? res.exchanges
              : Array.isArray(res)
                ? res
                : [];

        const total =
          Number(res?.total) ||
          Number(res?.meta?.total) ||
          Number(res?.pagination?.total) ||
          (Array.isArray(list) ? list.length : 0);

        return { total, exchange: list };
      }),
      catchError((res) => this.helper.catchError(res))
    );
  }

  getExchangeOrder(orderId: number): Observable<any> {
    return this.http
      .get<any>(environment.apiUrl + '/dealer-system/order/' + orderId)
      .pipe(catchError((res) => this.helper.catchError(res)));
  }

  getSingleExchange(orderId: number): Observable<any> {
    return this.http
      .get<any>(environment.apiUrl + '/exchanges/' + orderId)
      .pipe(catchError((res) => this.helper.catchError(res)));
  }

  getExchangeOptionsForProduct(productId: number): Observable<Array<{ id: number; name: string }>> {
    const cached = this.productOptionsCache.get(productId);
    if (cached) return cached;

    const req$ = this.http.get<any>(environment.apiUrl + '/exchanges/product/' + productId).pipe(
      map((res) => {
        const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        return arr
          .map((x: any) => {
            const translation = Array.isArray(x?.translations) ? x.translations[0] : undefined;
            const name = x?.name ?? translation?.content ?? '';
            return { id: Number(x?.id), name: String(name) };
          })
          .filter((x: any) => Number.isFinite(x.id) && x.name);
      }),
      catchError(() => of([] as Array<{ id: number; name: string }>)),
      shareReplay(1)
    );

    this.productOptionsCache.set(productId, req$);
    return req$;
  }

  createExchange(orderId: number, _payload: unknown): Observable<unknown> {
    return this.http
      .post<any>(environment.apiUrl + '/exchanges/order/' + orderId, _payload)
      .pipe(catchError((res) => this.helper.catchError(res)));
  }

  updateExchange(exchangeId: number, _payload: unknown): Observable<unknown> {
    return this.http
      .put<any>(environment.apiUrl + '/exchanges/' + exchangeId, _payload)
      .pipe(catchError((res) => this.helper.catchError(res)));
  }

  deleteSingleExchange(orderId: number): Observable<any> {
    return this.http
      .delete<any>(environment.apiUrl + '/exchanges/' + orderId)
      .pipe(catchError((res) => this.helper.catchError(res)));
  }

  exportExchange(gridData = new HttpParams()): Observable<any> {
    const qs = gridData?.toString?.() ?? '';
    const url = environment.apiUrl + '/exchanges/export' + (qs ? `?${qs}` : '');
    return this.http.get<any>(url).pipe(catchError((res) => this.helper.catchError(res)));
  }

  importExchange(file?: File, gridData: HttpParams = new HttpParams()): Observable<any> {
    const qs = gridData?.toString?.() ?? '';
    const url = environment.apiUrl + '/exchanges/import' + (qs ? `?${qs}` : '');
    if (!(file instanceof File)) {
      return this.http.post<any>(url, null).pipe(catchError((res) => this.helper.catchError(res)));
    }

    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.http.post<any>(url, fd).pipe(catchError((res) => this.helper.catchError(res)));
  }

}

